import { useEffect, useRef, useState } from 'react';

// Optimized scroll animation hook with better performance
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    triggerOnce = true,
    rootMargin = '0px 0px -30px 0px'
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Use a single shared IntersectionObserver for better performance
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, triggerOnce, rootMargin]);

  return [ref, isVisible];
};

// Simplified AnimatedSection wrapper - uses CSS transforms for GPU acceleration
export const AnimatedSection = ({
  children,
  className = '',
  animation = 'fade-up',
  delay = 0,
  ...props
}) => {
  const [ref, isVisible] = useScrollAnimation();

  const getAnimationClass = () => {
    const baseClass = 'transition-all duration-500 ease-out will-change-transform';

    if (!isVisible) {
      switch (animation) {
        case 'fade-up':
          return `${baseClass} opacity-0 translate-y-6`;
        case 'fade-down':
          return `${baseClass} opacity-0 -translate-y-6`;
        case 'fade-left':
          return `${baseClass} opacity-0 translate-x-6`;
        case 'fade-right':
          return `${baseClass} opacity-0 -translate-x-6`;
        case 'scale':
          return `${baseClass} opacity-0 scale-95`;
        case 'fade':
          return `${baseClass} opacity-0`;
        default:
          return `${baseClass} opacity-0 translate-y-6`;
      }
    }

    return `${baseClass} opacity-100 translate-y-0 translate-x-0 scale-100`;
  };

  return (
    <div
      ref={ref}
      className={`${getAnimationClass()} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...props}
    >
      {children}
    </div>
  );
};

export default useScrollAnimation;
